import { formatEther } from "@ethersproject/units";
import moment from "moment";

export const openLinkInNewTab = (link: string | undefined) =>
  window.open(link, "_blank");

export const copyToClipboard = (value: any) =>
  navigator.clipboard.writeText(value);

export const setLocalStorageValue = (key: string, value: any) =>
  window?.localStorage.setItem(key, JSON.stringify(value));

export const getLocalStorageValue = (key: string) =>
  window?.localStorage.getItem(key);

export const isFloat = (n: number) => {
  return Number(n) === n && n % 1 !== 0;
};

export const numberToCurrency = (value: string) => {
  return Math.abs(Number(value)) >= 1.0e9
    ? +(Math.abs(Number(value)) / 1.0e9).toFixed(2) + "B"
    : Math.abs(Number(value)) >= 1.0e6
    ? +(Math.abs(Number(value)) / 1.0e6).toFixed(2) + "M"
    : Math.abs(Number(value)) >= 1.0e3
    ? +(Math.abs(Number(value)) / 1.0e3).toFixed(2) + "K"
    : Math.abs(Number(value)).toString();
};

export const currencyToNumber = (value: string) => {
  if (!value) {
    return 0;
  }
  const c = value.slice(-1).toUpperCase();
  switch (c) {
    case "K":
      return +value.slice(0, -1) * 10e3;
    case "M":
      return +value.slice(0, -1) * 10e6;
    case "B":
      return +value.slice(0, -1) * 10e9;

    default:
      +value;
  }
};

export const toFixedWithoutRounding = (value: string, decimals: number) =>
  (value.match(new RegExp(`^-?\\d+(?:.\\d{0,${decimals}})?`)) as string[])[0];

export const formatEtherNumber = (value: string | undefined, decimals = 4) => {
  if (!value) {
    return -1;
  }
  const number = toFixedWithoutRounding(
    formatEther(value.toString()),
    decimals
  );

  return +number;
};

export const getDuration = (startTime: Date, endTime: Date): string => {
  const milliseconds = moment(endTime).diff(startTime);
  const duration = moment.duration(milliseconds);
  const durationAsHours: number = Math.floor(duration.asHours());
  const restInMinutes: number = duration.minutes();
  const hours: string =
    durationAsHours > 1
      ? `${durationAsHours} hrs`
      : durationAsHours > 0
      ? `${durationAsHours} hr`
      : "";

  if (hours) {
    return hours;
  }

  const minutes: string =
    restInMinutes === 1 ? `${restInMinutes} min` : `${restInMinutes} mins`;

  if (milliseconds >= 60000) {
    return minutes;
  }

  const seconds = `${moment.duration(milliseconds).seconds()} secs`;

  return seconds;
};

export const getNumbersFromValue = (value: string) => {
  const regex = /[+-]?\d+(\.\d+)?/g;

  const res = value.match(regex)?.map((v) => parseFloat(v))[0];

  return res || 0;
};

export const shortenAddress = (address: string) =>
  `${address.substring(0, 4)}...${address.substring(address.length - 4)}`;

export const isNumeric = (value: string) => {
  return /^\d+$/.test(value);
};

export const toStringPrecision = (num: number) => {
  let res = "";

  if (Math.abs(num) < 1.0) {
    const e = parseInt(num.toString().split("e-")[1]);
    if (e) {
      num *= Math.pow(10, e - 1);
      res = "0." + new Array(e).join("0") + num.toString().substring(2);
    }
  } else {
    let e = parseInt(num.toString().split("+")[1]);
    if (e > 20) {
      e -= 20;
      num /= Math.pow(10, e);
      res = num + new Array(e + 1).join("0");
    }
  }

  res = res.includes(".") ? Number(res).toFixed(8) : res;

  return res || num.toString();
};

export const isNumber = (str: string) => {
  if (str.trim() === "") {
    return false;
  }

  return !isNaN(+str);
};

export const getMessage = (error: Error) =>
  error.message
    .slice(
      0,
      error.message.indexOf("(") > 0
        ? error.message.indexOf("(")
        : error.message.length > 60
        ? 60
        : error.message.length
    )
    .trim();

export const validateEmail = (email: string) => {
  const emailReg = /^([\w-.]+@([\w-]+\.)+[\w-]{2,4})?$/;
  return emailReg.test(email);
};

export const getQueryString = (obj: any) => {
  const queryStringLength = Object.keys(obj).length || 0;
  return Object.keys(obj).reduce((acc, key, index) => {
    let str = acc;
    const value = obj[key];
    if (Array.isArray(value)) {
      if (value.length) {
        str += `${value
          .map((v) => `${key}=${encodeURIComponent(v)}&`)
          .join("")}`;
      }
    } else if (value) {
      str += `${key}=${encodeURIComponent(value)}`;
      if (index < queryStringLength - 1) {
        str += "&";
      }
    }

    return str;
  }, "");
};
